import React, { useState, useEffect } from 'react';
import { WellnessDimension as BaseDimension } from '../services/dimensionService';
import { ProgramSettingsService } from '../services/programSettingsService';
import { Icon } from './Icon';
import './ProgressLeader.css';

interface WellnessDimension extends Omit<BaseDimension, 'categoryId'> {
  percentage: number;
  categoryId?: string;
  categoryName?: string;
  categoryIcon?: string;
  categoryColor?: string;
}

interface ProgressLeaderProps {
  dimensions: WellnessDimension[];
  facilityId: string;
  primaryColor?: string;
  secondaryColor?: string;
}

export const ProgressLeader: React.FC<ProgressLeaderProps> = ({ 
  dimensions, 
  facilityId,
  primaryColor = '#667eea',
  secondaryColor = '#764ba2'
}) => {
  const [programName, setProgramName] = useState('Successful Aging');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadProgramSettings = async () => {
      try {
        const settings = await ProgramSettingsService.getProgramSettings(facilityId);
        setProgramName(settings.programName);
      } catch (error) {
        console.error('Failed to load program settings:', error);
        // Use default if loading fails
      } finally {
        setLoading(false);
      }
    };

    loadProgramSettings();
  }, [facilityId]);

  // Find the leading dimension/category
  const getLeadingItem = (): { name: string; icon: string; color: string; percentage: number } | null => {
    if (!dimensions || dimensions.length === 0) return null;

    // Check if any dimensions have category associations
    const hasCategoryAssociations = dimensions.some(d => d.categoryId);

    if (hasCategoryAssociations) {
      // Aggregate by category
      const categoryMap = new Map<string, { name: string; icon: string; color: string; percentage: number }>();

      dimensions.forEach(dim => {
        if (dim.categoryId && dim.categoryName) {
          const key = dim.categoryId;
          if (categoryMap.has(key)) {
            const existing = categoryMap.get(key)!;
            existing.percentage += dim.percentage;
          } else {
            categoryMap.set(key, {
              name: dim.categoryName,
              icon: dim.categoryIcon || dim.icon,
              color: dim.categoryColor || dim.color,
              percentage: dim.percentage
            });
          }
        }
      });

      // Include uncategorized dimensions
      dimensions.forEach(dim => {
        if (!dim.categoryId) {
          categoryMap.set(dim.id, {
            name: dim.name,
            icon: dim.icon,
            color: dim.color,
            percentage: dim.percentage
          });
        }
      });

      // Find the one with highest percentage
      let leader: { name: string; icon: string; color: string; percentage: number } | null = null;
      categoryMap.forEach(item => {
        if (!leader || item.percentage > leader.percentage) {
          leader = item;
        }
      });

      return leader;
    } else {
      // No category associations - find highest individual dimension
      let leader = dimensions[0];
      dimensions.forEach(dim => {
        if (dim.percentage > leader.percentage) {
          leader = dim;
        }
      });

      return {
        name: leader.name,
        icon: leader.icon,
        color: leader.color,
        percentage: leader.percentage
      };
    }
  };

  if (loading || !dimensions || dimensions.length === 0) {
    return null;
  }

  const leader = getLeadingItem();

  if (!leader) {
    return null;
  }

  return (
    <div 
      className="progress-leader"
      style={{
        background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`
      }}
    >
      <div className="progress-leader-content">
        <span className="progress-leader-emoji"><Icon name="FaTrophy" size={36} decorative /></span>
        <div className="progress-leader-text">
          <strong>Great work!</strong> Your progress towards your{' '}
          <span className="program-name">{programName}</span> goals is led by{' '}
          <span className="leader-name">
            <span className="leader-icon">
              <Icon name={leader.icon} size={20} decorative />
            </span>
            {leader.name}
          </span>.
        </div>
      </div>
    </div>
  );
};

